<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm rounded-4">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between p-3">
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-white me-3">
                  <vue-feather type="link" size="20"></vue-feather>
                </div>
                <div>
                  <h4 class="mb-0 fw-bold text-primary">Payment Integrations</h4>
                  <p class="text-muted mb-0 small">Manage your bank and payment gateway connections</p>
                </div>
              </div>
            </div>
          </template>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted">Loading integration data...</p>
          </div>

          <!-- No Integrations Message -->
          <div v-else-if="!organization.integrations?.length" class="p-4">
            <div class="alert alert-light border rounded-4 shadow-sm">
              <div class="d-flex align-items-center">
                <div class="icon-circle-sm bg-danger text-white me-3">
                  <vue-feather type="alert-circle" size="16"></vue-feather>
                </div>
                <div>
                  <h5 class="mb-1 fw-bold">No Active Integrations</h5>
                  <p class="mb-0">You don't have any payment integrations set up. Please contact our support team for activation.</p>
                  <div class="mt-3">
                    <Badge pill type="danger" class="badge-lg">
                      <vue-feather type="x-circle" size="14" class="me-1"></vue-feather>
                      Inactive
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Integration Cards -->
          <div v-else class="p-3">
            <div class="row g-4">
              <div v-for="integration in organization.integrations" :key="integration.id" class="col-12">
                <div class="integration-card card border-0 shadow-sm rounded-4">
                  <div class="card-header bg-light d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <div class="icon-circle-sm bg-primary text-white me-2">
                        <vue-feather :type="getIntegrationType(integration.type)" size="14"></vue-feather>
                      </div>
                      <h5 class="mb-0 fw-bold text-uppercase">{{ integration.type }}</h5>
                    </div>
                    <Badge pill type="success" class="badge-lg">
                      <vue-feather type="check-circle" size="14" class="me-1"></vue-feather>
                      {{ integration.status }}
                    </Badge>
                  </div>
                  <div class="card-body p-4">
                    <div class="row g-4">
                      <!-- Bank-specific fields -->
                      <div v-if="integration.type === 'bank'" class="col-md-6 col-lg-3">
                        <div class="info-card p-3 bg-light rounded-4 h-100">
                          <h6 class="mb-2 fw-bold d-flex align-items-center">
                            <vue-feather type="hash" size="14" class="me-2 text-primary"></vue-feather>
                            Account Number
                          </h6>
                          <p class="mb-0 fw-medium">{{ organization.account_no || 'N/A' }}</p>
                        </div>
                      </div>
                      <div v-if="integration.type === 'bank'" class="col-md-6 col-lg-3">
                        <div class="info-card p-3 bg-light rounded-4 h-100">
                          <h6 class="mb-2 fw-bold d-flex align-items-center">
                            <vue-feather type="map-pin" size="14" class="me-2 text-primary"></vue-feather>
                            Branch Name
                          </h6>
                          <p class="mb-0 fw-medium">{{ organization.branch_name || 'N/A' }}</p>
                        </div>
                      </div>
                      <div v-if="integration.type === 'bank'" class="col-md-6 col-lg-3">
                        <div class="info-card p-3 bg-light rounded-4 h-100">
                          <h6 class="mb-2 fw-bold d-flex align-items-center">
                            <vue-feather type="code" size="14" class="me-2 text-primary"></vue-feather>
                            Bank Code
                          </h6>
                          <p class="mb-0 fw-medium">{{ organization.bank_code || 'N/A' }}</p>
                        </div>
                      </div>
                      <div v-if="integration.type === 'bank'" class="col-md-6 col-lg-3">
                        <div class="info-card p-3 bg-light rounded-4 h-100">
                          <h6 class="mb-2 fw-bold d-flex align-items-center">
                            <vue-feather type="code" size="14" class="me-2 text-primary"></vue-feather>
                            Branch Code
                          </h6>
                          <p class="mb-0 fw-medium">{{ organization.branch_code || 'N/A' }}</p>
                        </div>
                      </div>

                      <!-- Meta fields -->
                      <div v-for="(value, key) in integration.meta" :key="key" class="col-md-6 col-lg-3">
                        <div class="info-card p-3 bg-light rounded-4 h-100">
                          <h6 class="mb-2 fw-bold d-flex align-items-center">
                            <vue-feather type="info" size="14" class="me-2 text-primary"></vue-feather>
                            {{ formatMetaKey(key) }}
                          </h6>
                          <p v-if="key === 'initiator_password'" class="mb-0 fw-medium">
                            <code>***************</code>
                          </p>
                          <p v-else class="mb-0 fw-medium">{{ value || 'N/A' }}</p>
                        </div>
                      </div>

                      <!-- Activation date -->
                      <div class="col-md-6 col-lg-3">
                        <div class="info-card p-3 bg-light rounded-4 h-100">
                          <h6 class="mb-2 fw-bold d-flex align-items-center">
                            <vue-feather type="calendar" size="14" class="me-2 text-primary"></vue-feather>
                            Activated On
                          </h6>
                          <p class="mb-0 fw-medium">{{ formatDate(integration.created_at) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Back Button -->
          <div class="d-flex justify-content-center pt-3 pb-4 border-top mx-3">
            <button class="btn btn-outline-dark rounded-pill" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>

import {decrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";
import VueFeather from 'vue-feather';

export default {
  name: "Integration",
  components: {
    VueFeather
  },

  data() {
    return {
      loading: true,
      organization: {
        integrations: []
      }
    }
  },

  methods: {
    formatDate,
    getIntegrationType(type) {
      switch(type.toLowerCase()) {
        case 'bank':
          return 'credit-card';
        case 'mpesa':
          return 'smartphone';
        case 'paypal':
          return 'dollar-sign';
        case 'stripe':
          return 'credit-card';
        default:
          return 'link';
      }
    },
    formatMetaKey(key) {
      // Convert snake_case or camelCase to Title Case with spaces
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },
    getOrganization() {
      this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
      this.loading = false;
    }
  },

  mounted() {
    this.getOrganization();
  },
}
</script>

<style scoped>
/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Card styles */
.integration-card {
  transition: all 0.3s ease;
}

.integration-card:hover {
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

.info-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.05);
}

.info-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* Badge styles */
.badge-lg {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

/* Button hover effects */
.btn-outline-dark.rounded-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .info-card {
    margin-bottom: 1rem;
  }
}
</style>
